import { Injectable, NgZone, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'
import { environment } from '../../../environments/environment'
import { FuseqService } from '../../FuseQ/fuseq.service'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'
import { FuseqSessionService } from '../../FuseQ/session.service'

export interface SharePointSnapshot {
  file: string
  site: string
  path: string
  sheet: string
  sheets: string[]
  headers: string[]
  rows: (string | number | boolean | null)[][]
  totalRows: number
  totalColumns: number
  truncated: boolean
  etag: string | null
  lastModified: string | null
  size: number | null
  fetchedAt: string
}

export type SharePointStreamHandler = (event: string, data: any) => void

@Injectable({
  providedIn: 'root'
})
export class SharePointViewerService {
  private readonly fuseqService = inject(FuseqService)
  private readonly fuseqSession = inject(FuseqSessionService)
  private readonly fuseqHeaders = inject(FuseqHeaders)
  private readonly http = inject(HttpClient)
  private readonly zone = inject(NgZone)

  private abortController: AbortController | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private stopped = true
  private generation = 0
  private reconnectAttempts = 0
  private activeSheet = 'AWP'

  streamUrl (sheet: string): string {
    return `${environment.API_URL}/internalAPI/sharepoint/sal-cisco/stream?sheet=${encodeURIComponent(sheet || 'AWP')}`
  }

  snapshotUrl (sheet: string): string {
    return `${environment.API_URL}/internalAPI/sharepoint/sal-cisco?sheet=${encodeURIComponent(sheet || 'AWP')}`
  }

  async loadSnapshot (sheet: string): Promise<SharePointSnapshot> {
    return await firstValueFrom(
      this.http.get<SharePointSnapshot>(this.snapshotUrl(sheet), this.fuseqHeaders.setTokenHeaders('GET'))
    )
  }

  start (onEvent: SharePointStreamHandler, onError: (message: string) => void, sheet: string): void {
    this.stop()
    this.stopped = false
    this.reconnectAttempts = 0
    this.activeSheet = sheet || 'AWP'
    const generation = ++this.generation
    this.open(onEvent, onError, generation)
  }

  stop (): void {
    this.stopped = true
    this.generation += 1
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  private buildHeaders (): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
      Authorization: this.fuseqService.getBackEndAuthentication()
    }
    const currentUser = this.fuseqSession.session
    if (currentUser?.token) {
      headers.AuthToken = currentUser.token
    }
    return headers
  }

  private open (onEvent: SharePointStreamHandler, onError: (message: string) => void, generation: number): void {
    if (this.stopped || generation !== this.generation) {
      return
    }

    const controller = new AbortController()
    this.abortController = controller

    fetch(this.streamUrl(this.activeSheet), {
      method: 'GET',
      headers: this.buildHeaders(),
      signal: controller.signal,
      cache: 'no-store'
    }).then(async (response) => {
      if (!response.ok) {
        const detail = await response.text().catch(() => '')
        throw new Error(`HTTP ${response.status}${detail ? ': ' + detail : ''}`)
      }
      if (!response.body) {
        throw new Error('Risposta SSE non leggibile')
      }
      await this.consume(response, onEvent)
      if (!this.stopped && generation === this.generation) {
        throw new Error('Stream SSE terminato')
      }
    }).catch((error) => {
      if (this.stopped || generation !== this.generation || error?.name === 'AbortError') {
        return
      }
      this.zone.run(() => onError(error?.message || 'Errore di connessione al file SharePoint'))
      this.scheduleReconnect(onEvent, onError, generation)
    })
  }

  private async consume (response: Response, onEvent: SharePointStreamHandler): Promise<void> {
    const reader = response.body!.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }
      buffer += decoder.decode(value, { stream: true })
      buffer = buffer.replace(/\r\n/g, '\n')

      let boundary = buffer.indexOf('\n\n')
      while (boundary >= 0) {
        const rawEvent = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)
        this.dispatch(rawEvent, onEvent)
        boundary = buffer.indexOf('\n\n')
      }
    }
  }

  private dispatch (rawEvent: string, onEvent: SharePointStreamHandler): void {
    let eventName = 'message'
    const dataLines: string[] = []

    for (const line of rawEvent.split('\n')) {
      if (!line || line.startsWith(':')) {
        continue
      }
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart())
      }
    }

    if (!dataLines.length) {
      return
    }

    const rawData = dataLines.join('\n')
    let data: any = rawData
    try {
      data = JSON.parse(rawData)
    } catch {
      // payload non JSON: resta una stringa
    }

    this.zone.run(() => onEvent(eventName, data))
    if (eventName === 'snapshot') {
      this.reconnectAttempts = 0
    }
  }

  private scheduleReconnect (onEvent: SharePointStreamHandler, onError: (message: string) => void, generation: number): void {
    if (this.stopped || generation !== this.generation || this.reconnectTimer) {
      return
    }
    const delay = Math.min(5000 * (2 ** this.reconnectAttempts), 30000)
    this.reconnectAttempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open(onEvent, onError, generation)
    }, delay)
  }
}
